import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Send as SendIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { 
  getDevicesNeedingUrgentMessages, 
  sendAllUrgentMessages,
  runUrgentMessageService 
} from '../services/urgentMessageService';
import { MaintenanceRecord } from '../utils/types';

interface UrgentMessageManagerProps {
  onMessagesSent?: () => void;
}

const UrgentMessageManager: React.FC<UrgentMessageManagerProps> = ({ onMessagesSent }) => {
  const [devicesNeedingMessages, setDevicesNeedingMessages] = useState<MaintenanceRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastResults, setLastResults] = useState<{
    totalProcessed: number;
    successCount: number;
    failureCount: number;
    errors: string[];
  } | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  // تحميل الأجهزة التي تحتاج رسائل استعجال
  const loadDevicesNeedingMessages = async () => {
    setLoading(true);
    try {
      const devices = await getDevicesNeedingUrgentMessages();
      setDevicesNeedingMessages(devices);
    } catch (error) {
      console.error('Error loading devices needing urgent messages:', error);
    } finally {
      setLoading(false);
    }
  };

  // إرسال جميع رسائل الاستعجال
  const handleSendAllMessages = async () => {
    setSending(true);
    try {
      const results = await sendAllUrgentMessages();
      setLastResults(results);
      
      // إعادة تحميل القائمة
      await loadDevicesNeedingMessages();
      
      if (onMessagesSent) {
        onMessagesSent();
      }
    } catch (error) {
      console.error('Error sending urgent messages:', error);
    } finally {
      setSending(false);
    }
  };

  // تشغيل الخدمة التلقائية
  const handleRunService = async () => {
    setSending(true);
    try {
      await runUrgentMessageService();
      await loadDevicesNeedingMessages();
      
      if (onMessagesSent) {
        onMessagesSent();
      }
    } catch (error) {
      console.error('Error running urgent message service:', error);
    } finally {
      setSending(false);
    }
  };

  // تحميل البيانات عند تحميل المكون
  useEffect(() => {
    loadDevicesNeedingMessages();
  }, []);

  // حساب الوقت المنقضي منذ اكتمال الإصلاح
  const getTimeSinceCompletion = (completedAt: Date | string): string => {
    const completed = new Date(completedAt);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - completed.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 24) {
      return `${diffInHours} ساعة`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays} يوم`;
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScheduleIcon />
            رسائل الاستعجال التلقائية
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="تحديث القائمة">
              <IconButton onClick={loadDevicesNeedingMessages} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="عرض تفاصيل آخر عملية إرسال">
              <IconButton 
                onClick={() => setShowDetailsDialog(true)}
                disabled={!lastResults}
              >
                <InfoIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          يتم إرسال رسائل تذكير تلقائية للعملاء الذين لم يستلموا أجهزتهم المكتملة خلال يومين، 
          وتتكرر كل 48 ساعة حتى الاستلام.
        </Typography>

        {/* عرض الإحصائيات */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Chip 
            label={`${devicesNeedingMessages.length} جهاز يحتاج رسالة`}
            color={devicesNeedingMessages.length > 0 ? 'warning' : 'success'}
            icon={devicesNeedingMessages.length > 0 ? <ScheduleIcon /> : <CheckCircleIcon />}
          />
          
          {lastResults && (
            <Chip 
              label={`آخر إرسال: ${lastResults.successCount}/${lastResults.totalProcessed}`}
              color={lastResults.failureCount > 0 ? 'error' : 'success'}
              icon={lastResults.failureCount > 0 ? <ErrorIcon /> : <CheckCircleIcon />}
            />
          )}
        </Box>

        {/* أزرار الإجراءات */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button
            variant="contained"
            startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
            onClick={handleSendAllMessages}
            disabled={sending || devicesNeedingMessages.length === 0}
            color="primary"
          >
            {sending ? 'جاري الإرسال...' : `إرسال ${devicesNeedingMessages.length} رسالة`}
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<ScheduleIcon />}
            onClick={handleRunService}
            disabled={sending}
          >
            تشغيل الخدمة التلقائية
          </Button>
        </Box>

        {/* قائمة الأجهزة */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : devicesNeedingMessages.length > 0 ? (
          <List>
            {devicesNeedingMessages.map((device, index) => (
              <React.Fragment key={device.id}>
                <ListItem>
                  <ListItemIcon>
                    <ScheduleIcon color="warning" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${device.customerName} - ${device.deviceType}`}
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          رقم الطلب: {device.orderNumber ? `#${device.orderNumber}` : device.id.substring(0, 8)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          مكتمل منذ: {device.completedAt ? getTimeSinceCompletion(device.completedAt) : 'غير محدد'}
                        </Typography>
                        {device.lastUrgentMessageSentAt && (
                          <Typography variant="body2" color="text.secondary">
                            آخر رسالة: {getTimeSinceCompletion(device.lastUrgentMessageSentAt)} مضت
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {index < devicesNeedingMessages.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Alert severity="success" sx={{ mt: 2 }}>
            لا توجد أجهزة تحتاج لرسائل استعجال في الوقت الحالي
          </Alert>
        )}

        {/* حوار تفاصيل آخر عملية إرسال */}
        <Dialog 
          open={showDetailsDialog} 
          onClose={() => setShowDetailsDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>تفاصيل آخر عملية إرسال</DialogTitle>
          <DialogContent>
            {lastResults ? (
              <Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>إجمالي الرسائل المعالجة:</strong> {lastResults.totalProcessed}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>تم الإرسال بنجاح:</strong> {lastResults.successCount}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>فشل في الإرسال:</strong> {lastResults.failureCount}
                </Typography>
                
                {lastResults.errors.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      الأخطاء:
                    </Typography>
                    <List dense>
                      {lastResults.errors.map((error, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <ErrorIcon color="error" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={error}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </Box>
            ) : (
              <Typography>لا توجد بيانات متاحة</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDetailsDialog(false)}>
              إغلاق
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default UrgentMessageManager;
